/**
 * weitzman-product.js
 * PDP interactions: gallery thumbnails, quantity stepper, native variant
 * picker, progressive-enhanced add-to-cart, recently-viewed recording,
 * native recommendations swap, description toggle, size guide toggle, and
 * click-to-zoom on the main image. Reads product/label data set by this
 * section's own small inline <script> (window.__wtzPdpConfig) since this
 * file loads deferred and Liquid cannot interpolate into an external asset.
 */
(function () {
  const wtzPdpConfig = window.__wtzPdpConfig || {};

  const wrapper = document.querySelector('.wtz-pdp-wrapper');
  if (!wrapper || wrapper.dataset.wired === '1') return;
  wrapper.dataset.wired = '1';

  /* Gallery thumbnails */
  const activeImg = wrapper.querySelector('[data-pdp-active-img]');
  wrapper.querySelectorAll('[data-pdp-thumbs] .wtz-pdp-thumb-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      wrapper.querySelectorAll('[data-pdp-thumbs] .wtz-pdp-thumb-btn').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      if (activeImg) {
        activeImg.srcset = btn.dataset.fullSrcset || '';
        activeImg.src = btn.dataset.full;
        activeImg.alt = btn.dataset.alt || '';
      }
    });
  });

  /* Gift card "send as a gift" recipient form: reveal the fields, require
     a recipient email only while the checkbox is checked, floor the "Send
     on" date picker at today, and stamp the buyer's timezone offset (raw
     getTimezoneOffset() minutes, as Dawn's recipient form sends it) into the hidden
     __shopify_offset property so a "send on" date lands on the right day
     for the recipient. */
  const recipientForm = wrapper.querySelector('[data-recipient-form]');
  if (recipientForm) {
    const recipientCheckbox = recipientForm.querySelector('[data-recipient-checkbox]');
    const recipientFields = recipientForm.querySelector('[data-recipient-fields]');
    const recipientEmail = recipientForm.querySelector('[data-recipient-email]');
    const recipientSendOn = recipientForm.querySelector('[data-recipient-send-on]');
    const recipientOffset = recipientForm.querySelector('[data-recipient-offset]');
    if (recipientSendOn) recipientSendOn.min = new Date().toISOString().slice(0, 10);
    if (recipientOffset) recipientOffset.value = new Date().getTimezoneOffset().toString();
    if (recipientCheckbox && recipientFields && recipientEmail) {
      /* The fields sit inside the product form, so while the box is
         unchecked they're disabled too: otherwise a typed-then-abandoned
         email still posts as a line item property. */
      const syncRecipient = function () {
        const on = recipientCheckbox.checked;
        recipientFields.hidden = !on;
        recipientEmail.required = on;
        recipientFields.querySelectorAll('input, textarea').forEach(function (el) { el.disabled = !on; });
      };
      recipientCheckbox.addEventListener('change', syncRecipient);
      syncRecipient();
    }
  }

  /* Quantity stepper */
  const qtyInput = wrapper.querySelector('[data-qty-input]');
  const minusBtn = wrapper.querySelector('[data-qty-minus]');
  const plusBtn = wrapper.querySelector('[data-qty-plus]');
  if (qtyInput && minusBtn && plusBtn) {
    minusBtn.addEventListener('click', function () {
      const v = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = Math.max(1, v - 1);
    });
    plusBtn.addEventListener('click', function () {
      const v = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = v + 1;
    });
  }

  /* Native variant picker: swap between buttons and selects, keep the
     hidden id input, price, compare-at price, and add-to-cart state in
     sync with the selected combination. ponytail: does not deep-link the
     URL to the selected variant (?variant=), the PDP works fine without
     it, add history.replaceState here if SEO/back-button parity is later
     required. */
  const variants = wtzPdpConfig.variants || [];
  const optionsWrap = wrapper.querySelector('[data-pdp-options]');
  const variantInput = wrapper.querySelector('[data-pdp-variant-input]');
  const priceEl = wrapper.querySelector('[data-pdp-price]');
  const compareEl = wrapper.querySelector('[data-pdp-compare]');
  const memberPriceEl = wrapper.querySelector('[data-pdp-member-price]');
  const addBtn = wrapper.querySelector('[data-pdp-add-btn]');
  const addBtnLabel = wrapper.querySelector('[data-add-btn-label]');
  const notifyMe = wrapper.querySelector('[data-notify-me]');
  const addToBagLabel = wtzPdpConfig.addToBagLabel;
  const soldOutLabel = wtzPdpConfig.soldOutLabel;
  const paymentBtn = wrapper.querySelector('.shopify-payment-button');

  function currentSelection() {
    const selected = [];
    if (!optionsWrap) return selected;
    optionsWrap.querySelectorAll('.wtz-option-group').forEach(function (group) {
      const select = group.querySelector('select');
      if (select) { selected.push(select.value); return; }
      const active = group.querySelector('.wtz-swatch-btn.is-selected');
      selected.push(active ? active.dataset.value : null);
    });
    return selected;
  }

  function findVariant(selected) {
    return variants.find(function (v) {
      return selected.every(function (val, i) {
        return val === null || v['option' + (i + 1)] === val;
      });
    });
  }

  /* A combination with no matching variant (e.g. Rose Gold + Everything on
     a product that only has three of six combos). Leaving the previous id in
     the hidden input meant Add to Cart, and the Shop Pay button that reads the
     same input, bought a variant the shopper had not selected. */
  function applyUnavailable() {
    if (variantInput) variantInput.value = '';
    if (addBtn) {
      addBtn.disabled = true;
      if (addBtnLabel) addBtnLabel.textContent = wtzPdpConfig.unavailableLabel || 'Unavailable';
    }
    if (notifyMe) notifyMe.hidden = true;
    if (paymentBtn) paymentBtn.style.display = 'none';
  }

  function applyVariant(v) {
    if (!v) { applyUnavailable(); return; }
    if (paymentBtn) paymentBtn.style.display = '';
    const money = window.WTZ.money;
    const withWidth = window.WTZ.withWidth;
    const srcsetFor = window.WTZ.srcsetFor;
    if (variantInput) variantInput.value = v.id;
    if (priceEl) priceEl.textContent = money(v.price);
    if (memberPriceEl) memberPriceEl.textContent = money(Math.round(v.price * (100 - (wtzPdpConfig.memberPercent || 0)) / 100));
    if (compareEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        compareEl.textContent = money(v.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (addBtn) {
      addBtn.disabled = !v.available;
      if (addBtnLabel) {
        addBtnLabel.textContent = v.available ? (addToBagLabel + ' • ' + money(v.price)) : soldOutLabel;
      }
    }
    if (notifyMe) notifyMe.hidden = v.available;
    if (v.featured_image && activeImg) {
      activeImg.srcset = srcsetFor(v.featured_image.src);
      activeImg.src = withWidth(v.featured_image.src, 1200);
      activeImg.alt = v.featured_image.alt || activeImg.alt;
      wrapper.querySelectorAll('[data-pdp-thumbs] .wtz-pdp-thumb-btn').forEach(function (b) {
        b.classList.toggle('is-active', Number(b.dataset.imageId) === v.featured_image.id);
      });
    }
  }

  if (optionsWrap) {
    optionsWrap.addEventListener('click', function (e) {
      const btn = e.target.closest('.wtz-swatch-btn');
      if (!btn) return;
      const group = btn.closest('.wtz-option-group');
      if (!group) return;
      group.querySelectorAll('.wtz-swatch-btn').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      const label = group.querySelector('[data-selected-value]');
      if (label) label.textContent = btn.dataset.value;
      applyVariant(findVariant(currentSelection()));
    });
    optionsWrap.addEventListener('change', function (e) {
      const select = e.target.closest('select');
      if (!select) return;
      const group = select.closest('.wtz-option-group');
      if (!group) return;
      const label = group.querySelector('[data-selected-value]');
      if (label) label.textContent = select.value;
      applyVariant(findVariant(currentSelection()));
    });
  }

  /* Progressive-enhance the add-to-cart form: fetch cart/add.js so it does
     not force a page reload, but the plain <form action="/cart/add"> still
     works if JS fails to load. Dispatches cart:updated for whichever cart
     drawer the header build wires up. */
  const form = wrapper.querySelector('[data-wz-product-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      /* Shopify's dynamic checkout buttons live in this same form and reach
         checkout by submitting it. Only this theme's own Add to Cart takes the
         AJAX path; everything else is left to Shopify.

         The test is for an explicit name="add" submitter rather than "not a
         wallet button", because a wallet that calls requestSubmit() or
         form.submit() produces a submit event with NO submitter at all. Reading
         e.submitter.name on that is undefined, so a guard shaped as
         `e.submitter && e.submitter.name !== 'add'` falls through and swallows
         the checkout into a silent add-to-cart that never navigates. That is a
         mobile-first failure, since the wallet sheet is where those
         programmatic submits happen.

         The no-JS path is unaffected: without JS this listener never runs and
         the form POSTs to /cart/add as normal. */
      if (!e.submitter || e.submitter.name !== 'add') return;
      /* weitzman-cart.js missing (blocked, failed to load): leave the native
         POST to /cart/add alone rather than cancel it and call nothing. */
      if (!window.WTZ || !window.WTZ.addToCart) return;
      e.preventDefault();
      /* preventDefault above skips the browser's own required/type=email
         validation UI, so the recipient email (only required once "send
         as a gift" is checked) needs its own check before the AJAX add. */
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      if (addBtn) addBtn.disabled = true;
      const originalLabel = addBtnLabel ? addBtnLabel.textContent : '';
      /* Immediate state change on tap. Without it the button looks inert for
         a whole round trip and shoppers tap it repeatedly. */
      if (addBtnLabel) addBtnLabel.textContent = wtzPdpConfig.addingLabel || 'Adding...';
      /* WTZ.addToCart (weitzman-cart.js) posts, announces the add and shows
         the confirmation toast, so it is read out and stays visible even when
         the drawer opens over this area. The button just returns to normal. */
      const restore = function () {
        if (addBtnLabel) addBtnLabel.textContent = originalLabel;
        if (addBtn) addBtn.disabled = false;
      };
      window.WTZ.addToCart(new FormData(form))
        .then(restore)
        .catch(function (err) {
          /* Shopify refused the add (sold out, over stock): say why. */
          if (err.description !== undefined) {
            window.WTZ.toast(err.description || wtzPdpConfig.addFailedLabel || 'Could not add to cart', { type: 'error' });
            restore();
            return;
          }
          /* FAIL-OPEN. A blocked fetch, offline blip or extension-injected
             error must not dead-end the only add-to-cart on the page. This is
             a real <form action="/cart/add" method="post"> with a hidden
             name="id", so handing the submit back to the browser performs a
             native POST and Shopify redirects to /cart. form.submit() is used
             deliberately: it does not re-fire the submit event, so this
             listener cannot loop. */
          if (addBtnLabel) addBtnLabel.textContent = wtzPdpConfig.addingLabel || 'Adding...';
          try {
            form.submit();
          } catch (err) {
            var toast = window.WTZ && window.WTZ.toast;
            if (toast) toast(wtzPdpConfig.addFailedLabel || 'Could not add. Try again', { type: 'error' });
            if (addBtnLabel) addBtnLabel.textContent = originalLabel;
            if (addBtn) addBtn.disabled = false;
          }
        });
    });
  }

  /* Recently viewed: record this product for the localStorage rail. */
  try {
    const key = 'wtz_recently_viewed';
    const handle = wtzPdpConfig.productHandle;
    const list = JSON.parse(localStorage.getItem(key) || '[]').filter(function (h) { return h !== handle; });
    list.unshift(handle);
    localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
  } catch (err) { /* ponytail: storage can be unavailable (private mode), fail silent */ }

  /* Native product recommendations: fetch the section's own recommended
     markup and swap it in, matches Dawn's product-recommendations pattern. */
  const recEl = wrapper.parentElement ? wrapper.parentElement.querySelector('[data-recommendations]') : document.querySelector('[data-recommendations]');
  if (recEl && recEl.dataset.url) {
    fetch(recEl.dataset.url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-recommendations]');
        if (next && next.innerHTML.trim() !== '') recEl.innerHTML = next.innerHTML;
      })
      .catch(function () { /* ponytail: recommendations are progressive, silently skip on failure */ });
  }

  const descToggle = wrapper.querySelector('[data-pdp-desc-toggle]');
  if (descToggle) {
    descToggle.addEventListener('click', function () {
      const desc = wrapper.querySelector('[data-pdp-description]');
      const full = wrapper.querySelector('[data-pdp-desc-full]');
      const expanded = descToggle.getAttribute('aria-expanded') === 'true';
      full.hidden = expanded;
      desc.classList.toggle('is-expanded', !expanded);
      descToggle.setAttribute('aria-expanded', String(!expanded));
      descToggle.textContent = expanded ? 'See full description' : 'Show less';
    });
  }

  /* Size guide toggle: opens/closes the inline panel under the swatches. */
  const sizeGuideToggle = wrapper.querySelector('[data-size-guide-toggle]');
  if (sizeGuideToggle) {
    const sizeGuidePanel = document.getElementById(sizeGuideToggle.getAttribute('aria-controls'));
    sizeGuideToggle.addEventListener('click', function () {
      const expanded = sizeGuideToggle.getAttribute('aria-expanded') === 'true';
      sizeGuideToggle.setAttribute('aria-expanded', String(!expanded));
      if (sizeGuidePanel) sizeGuidePanel.hidden = expanded;
    });
  }

})();

/* Click-to-zoom on the main PDP image, every product page. */
(function () {
  const frame = document.querySelector('.wtz-pdp-main-frame');
  if (!frame || frame.dataset.zoomWired === '1') return;
  frame.dataset.zoomWired = '1';

  /* The frame is a div, so it is invisible to the keyboard on its own.
     Giving it a button role plus a tab stop, rather than rewriting the
     markup as a real <button>, because the frame also holds the badge
     overlay and nesting interactive elements inside a button is invalid. */
  frame.setAttribute('role', 'button');
  frame.setAttribute('tabindex', '0');
  frame.setAttribute('aria-label', frame.dataset.zoomLabel || 'Zoom product image');

  const overlay = document.createElement('div');
  overlay.className = 'wtz-zoom-overlay';
  overlay.setAttribute('data-pdp-zoom-overlay', '');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', frame.dataset.zoomLabel || 'Zoomed product image');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'wtz-zoom-close';
  closeBtn.setAttribute('aria-label', 'Close zoomed image');
  closeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const zoomImg = document.createElement('img');
  overlay.appendChild(zoomImg);
  overlay.appendChild(closeBtn);
  document.body.appendChild(overlay);

  /* Where focus was before the overlay took it, so it can go back. Without
     this a keyboard user is returned to the top of the document and has to
     tab all the way down to where they were. */
  let lastFocus = null;

  function openZoom() {
    const activeImg = frame.querySelector('img[data-pdp-active-img]');
    if (!activeImg) return;
    zoomImg.src = activeImg.src;
    zoomImg.srcset = activeImg.srcset || '';
    zoomImg.alt = activeImg.alt || '';
    lastFocus = document.activeElement;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    closeBtn.focus();
  }

  function closeZoom() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  frame.addEventListener('click', function (e) {
    if (e.target.closest('.wtz-pdp-badge-square')) return;
    openZoom();
  });
  /* Enter and Space are what a real button responds to; preventDefault on
     Space stops the page scrolling underneath. */
  frame.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    if (e.target.closest('.wtz-pdp-badge-square')) return;
    e.preventDefault();
    openZoom();
  });
  overlay.addEventListener('click', closeZoom);
  closeBtn.addEventListener('click', function (e) { e.stopPropagation(); closeZoom(); });
  document.addEventListener('keydown', function (e) {
    if (!overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeZoom(); return; }
    /* Only the close button is focusable inside the overlay, so the trap is
       simply keeping Tab on it rather than letting focus walk the page
       behind the dialog. */
    if (e.key === 'Tab') { e.preventDefault(); closeBtn.focus(); }
  });

  /* Notify me: enrols the shopper in Klaviyo's Back in Stock flow for the
     selected variant. The button only tells them they are subscribed once
     Klaviyo has accepted the request.

     This block lives in a different IIFE from the buy-box code, so it reads
     the config and the variant input from the document rather than from that
     closure's `wtzPdpConfig` and `wrapper`, which are not in scope here. */
  const notifyConfig = window.__wtzPdpConfig || {};

  const notifyForm = document.querySelector('[data-notify-form]');
  if (notifyForm) {
    notifyForm.addEventListener('submit', function (e) {
      e.preventDefault();
      const emailInput = notifyForm.querySelector('[data-notify-email]');
      const btn = notifyForm.querySelector('[data-notify-btn]');
      /* Scoped to the form rather than the document: a theme-editor reorder
         or a second product section would otherwise write the confirmation
         into the wrong instance's status line. */
      const status = notifyForm.parentElement
        ? notifyForm.parentElement.querySelector('[data-notify-status]')
        : null;
      if (!emailInput || !btn) return;
      const email = (emailInput.value || '').trim();
      if (!email) return;
      const vid = document.querySelector('[data-pdp-variant-input]');
      const variantId = vid ? vid.value : '';
      btn.disabled = true;
      btn.textContent = 'Subscribing...';

      function report(message, ok) {
        if (status) {
          status.textContent = message;
          status.className = 'wtz-notify-me__status ' + (ok ? 'is-success' : 'is-error');
          status.hidden = false;
        }
        if (!ok) {
          const toast = window.WTZ && window.WTZ.toast;
          if (toast) toast(message, { type: 'error' });
        }
      }

      /* Klaviyo's real back-in-stock subscription, not a tracked event. The
         previous version pushed a "Back In Stock Subscribe" metric, which
         records that someone asked but never enrols them, so Klaviyo's own
         restock notification never fires. This endpoint is the one the Back
         in Stock flow listens to. It takes the public site ID rather than a
         private key precisely because it is meant to be called from a
         storefront, and answers 202 with an empty body.

         The variant id has to be Klaviyo's catalog form, not the bare
         Shopify id: integration ::: catalog ::: external id. */
      const companyId = notifyConfig.klaviyoCompanyId;
      if (!companyId || !variantId) {
        report(notifyConfig.notifyFailedLabel || 'We could not save your request just now. Please try again in a moment.', false);
        btn.disabled = false;
        btn.textContent = 'Notify Me';
        return;
      }

      /* Identify the profile too, so the address is attached to the same
         person the rest of the site tracks rather than sitting only against
         the subscription. Best effort: it must not gate the enrolment. */
      const kl = window.klaviyo || window._learnq;
      if (kl && kl.push) kl.push(['identify', { '$email': email }]);

      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-07-15'
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email }
                }
              }
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: (notifyConfig.klaviyoVariantPrefix || '$shopify:::$default:::') + variantId
                }
              }
            }
          }
        })
      })
        .then(function (r) {
          if (r.status === 202 || r.ok) {
            report(notifyConfig.notifySuccessLabel || 'You will be notified when this item is back in stock.', true);
            btn.textContent = 'Subscribed';
            emailInput.value = '';
            return;
          }
          throw new Error('HTTP ' + r.status);
        })
        .catch(function (err) {
          console.error('Back in stock subscribe failed:', err);
          report(notifyConfig.notifyFailedLabel || 'We could not save your request just now. Please try again in a moment.', false);
          btn.disabled = false;
          btn.textContent = 'Notify Me';
        });
    });
  }
})();
